<script setup>
import { userStore } from '@/stores/user'
import { RouterView, useRouter } from 'vue-router'
import { ref, computed, onMounted } from 'vue';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import ButtonGroup from 'primevue/buttongroup';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import Divider from 'primevue/divider';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { useConfirm } from "primevue/useconfirm";
import { useToast } from 'primevue/usetoast';
import Knob from 'primevue/knob';
import { fetchFromApi } from '@/fetch.ts';

const user = userStore();
const router = useRouter();
const toast = useToast();

const CREDIT_USAGE_WARNING_THRESHOLD = 75; // percentage
const CREDIT_USAGE_DANGER_THRESHOLD = 90; // percentage

const props = defineProps({
    subscription: {
        type: Object,
        required: true
    },
    bookmakers: {
        type: Array,
        default: () => []
    }
});
const subscription = ref(props.subscription);

const formatLocalTimestamp = (utcTimestamp) => {
  const date = new Date(utcTimestamp);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const accessKey = ref('')
onMounted(() => {
    accessKey.value = subscription.value.access_key;

    formBookmakers.value = formattedSavedAffiliateLinks.value.map(item => {
        return Object.assign({}, item) // clone
    })

    showAttribution.value = subscription.value.custom_data?.show_attribution || true
});

const reqTotal = computed(() => {
    return subscription.value.usage.req_used + subscription.value.usage.req_remaining;
});

const reqUsedPercent = computed(() => {
    return (subscription.value.usage.req_used * 100 / reqTotal.value).toFixed(0);
});

const usageBarColor = computed(() => {
    const percent = Number(reqUsedPercent.value);
    if (percent >= CREDIT_USAGE_DANGER_THRESHOLD) return '#ef4444';
    if (percent >= CREDIT_USAGE_WARNING_THRESHOLD) return '#f59e0b';
    return '#22c55e';
});

const isHighUsage = computed(() => {
    return Number(reqUsedPercent.value) >= CREDIT_USAGE_DANGER_THRESHOLD;
});

const isOverLimit = computed(() => {
    return subscription.value.usage.req_remaining <= 0;
});

const confirm = useConfirm();

const redirecting = ref(false);
const redirectError = ref(null);
async function manageBilling() {
    redirecting.value = true;    
    redirectError.value = null;

    try {
        const response = await fetchFromApi(`/widget/subscriptions/${subscription.value.id}/create-customer-portal-session?redirect_to_dash=true`, 'POST');
        if (response.error) {
            redirectError.value = 'Error redirecting to billing: ' + response.error;
            console.error('Error redirecting to billing:', response.error);
            return;
        }
        window.location = response.data.url;
    } catch (error) {
        redirectError.value = 'Something went wrong';
        console.error('Error redirecting to billing:', error);
        return;
    } finally {
        redirecting.value = false;
    }
}

function copyAccessKey(key) {
    navigator.clipboard.writeText(key).then(() => {
        toast.add({ severity: 'success', summary: 'Copied', detail: 'Access key copied to clipboard', life: 3000 });
    }).catch(err => {
        console.error('Failed to copy access key: ', err);
    });
}


// Custom Data

const configOptionsShowing = ref(false);
const formBookmakers = ref([]);
const showAttribution = ref(true);
const affiliateLinks = ref({});

const savedAffiliateLinks = computed(() => {
    return subscription.value.custom_data?.affiliate_links || {}
});

const formattedSavedAffiliateLinks = computed(() => {
    return Object.keys(savedAffiliateLinks.value).map(bookmakerKey => {
        return {
            key: bookmakerKey,
            link: savedAffiliateLinks.value[bookmakerKey],
        }
    })
});

const allBookmakers = computed(() => props.bookmakers);

const allBookmakersUnique = computed(() => {
    // ideally bookmaker keys would not be duplicated across regions
    // And the option list would group by region
    // In the meantime we'll have to de-duplicate
    const uniqueBookmakerKeys = new Set();
    return allBookmakers.value.filter(bookmaker => {
        if (uniqueBookmakerKeys.has(bookmaker.key)) {
            return false
        }

        uniqueBookmakerKeys.add(bookmaker.key)
        return bookmaker
    }).sort((a,b) => {return a.key > b.key})
});

const customDataSaving = ref(false);
const customDataSavingError = ref(null);
async function handleSaveClick() {
    if (customDataSaving.value === true) {
        return
    }
    customDataSaving.value = true
    customDataSavingError.value = null

    const payload = {
        affiliate_links: formBookmakers.value.filter(item => {
            return item.key !== null && item.link !== ''
        }),
        show_attribution: showAttribution.value,
    }

    try {
        const response = await fetchFromApi(`/widget/subscriptions/${subscription.value.id}/custom-data`, 'PUT', payload)
        if (response.error) {
            if (response.status_code === 422) {
                if (response.error[0].type === 'value_error.url.scheme') {
                    customDataSavingError.value = 'Validation error: Please ensure all links are proper URLs, starting with either http or https';
                } else {
                    customDataSavingError.value = `Validation error: ${response.error[0].msg}`;
                }
            } else if (response.status_code === 403) {
                customDataSavingError.value = response.error || 'Something went wrong';
            } else {
                console.error('Error saving custom data:', response.error);
                customDataSavingError.value = 'An error occurred while saving custom data. Please try again soon, and contact us if this persists.';
            }
            return;
        }

        toast.add({ severity: 'success', summary: 'Saved', detail: 'Data saved', life: 3000 });
    } catch (error) {
        console.error('Error saving custom data:', error);
        customDataSavingError.value = 'An error occurred while saving custom data. Please try again soon, and contact us if this persists.';
    } finally {
        customDataSaving.value = false
    }
}
function removeAffiliateLink(index) {
    formBookmakers.value.splice(index, 1)
}
function addFormBookmaker() {
    formBookmakers.value.push({
        key: null,
        link: ''
    })
}
function resetLinks() {
    // use map for clone
    formBookmakers.value = formattedSavedAffiliateLinks.value.map(item => {
        return Object.assign({}, item) // clone
    })
}

</script>

<template>
    <Card class="mx-auto my-5 subscription-card shadow-sm border border-slate-200 rounded-xl" style="max-width: 800px;">
        <template #title>
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <span class="text-lg font-semibold">{{ subscription.plan_details.name }}</span>
                <Badge severity="success" value="Active" v-if="subscription.status === 'active'" />
                <Badge severity="secondary" value="Paused" v-else-if="subscription.status === 'paused'" />
                <Badge severity="warn" value="Due to Cancel" v-else-if="subscription.status === 'due_to_cancel'" />
                <Badge severity="danger" value="Payment Failed" v-else-if="subscription.status === 'payment_failed'" />
                <Badge severity="secondary" value="Cancelled" v-else-if="subscription.status === 'cancelled'" />
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-6">
                <Message severity="warn" v-if="subscription.status === 'due_to_cancel'" :closable="false">
                    <div v-if="subscription.cancel_at_timestamp_utc">
                        Subscription is due to cancel on {{ formatLocalTimestamp(subscription.cancel_at_timestamp_utc) }}
                    </div>
                    <div v-else>
                        Subscription is due to cancel before the next billing cycle.
                    </div>
                </Message>
                <Message severity="error" v-else-if="subscription.status === 'payment_failed'" :closable="false">
                    The last payment attempt was unsuccessful.
                    <br />
                    <div v-if="subscription.payment_failed_user_facing_reason">
                        Reason: <code>{{ subscription.payment_failed_user_facing_reason }}</code>
                    </div>
                    <div v-if="subscription.next_payment_attempt_timestamp_utc">
                        Next payment attempt: {{ formatLocalTimestamp(subscription.next_payment_attempt_timestamp_utc) }}
                    </div>
                    <div v-if="subscription.hosted_invoice_url">
                        <span class="underline cursor-pointer" @click="window.location = subscription.hosted_invoice_url">View invoice</span> to update billing information and retry payment.
                    </div>
                    <div v-else>
                        To update billing details, <span class="underline cursor-pointer" @click="manageBilling()">Manage Subscription</span>
                    </div>
                </Message>
                <Message variant="simple" size="small" severity="secondary" v-else-if="subscription.status === 'cancelled'" :closable="false">
                    Subscription has ended. To regain access to the widget, a <a @click="$emit('showNewSubscription')" class="underline cursor-pointer" >new subscription</a> is needed.
                </Message>

                <!-- Access key -->
                <section>
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-key"></i>
                        <span>Access Key</span>
                    </div>
                    <div class="flex flex-row items-center gap-2">
                        <code class="relative flex-1 min-w-0 py-2 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm">
                            <span class="block overflow-x-auto whitespace-nowrap pl-3 pr-3">
                                {{ accessKey }}
                            </span>
                        </code>
                        <ButtonGroup class="shrink-0">
                            <Button
                                v-if="subscription.access_key_enabled === true"
                                severity="secondary"
                                icon="pi pi-copy"
                                @click="copyAccessKey(accessKey)"
                                label="Copy"
                                outlined
                                size="small"
                            />
                        </ButtonGroup>
                    </div>
                </section>

                <!-- Usage -->
                <section v-if="subscription.status !== 'cancelled'">
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-chart-bar"></i>
                        <span>Usage this period</span>
                    </div>
                    <div class="flex items-center gap-4">
                        <Knob
                            :modelValue="Number(reqUsedPercent)"
                            readonly
                            :size="70"
                            valueTemplate="{value}%"
                            :valueColor="usageBarColor"
                            rangeColor="#e2e8f0"
                        />
                        <div>
                            <span class="text-md font-bold tabular-nums">
                                {{ subscription.usage.req_used.toLocaleString() }}
                                <span class="text-sm text-slate-500">used of</span>
                                {{ reqTotal.toLocaleString() }} credits
                            </span>
                            <p class="text-xs text-slate-400 mt-1">
                                Monthly plans reset on the 1st of each month at 12AM UTC
                            </p>
                        </div>
                    </div>
                    <Message severity="warn" :closable="false" size="small" class="mt-3" v-if="isHighUsage">
                        <template v-if="isOverLimit">Credit limit reached.</template>
                        <template v-else>You're approaching the monthly credit limit.</template>
                        If you need more credits this period, <a href="https://the-odds-api.com/manage/upgrade-downgrade-cancel-a-subscription.html" target="_blank" rel="noopener" class="text-sm underline cursor-pointer font-medium">upgrades are prorated</a>.
                    </Message>
                </section>

                <!-- Plan details -->
                <section>
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-info-circle"></i>
                        <span>Plan Details</span>
                    </div>
                    <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                        <dt class="text-slate-400">Price</dt>
                        <dd v-if="Number(subscription.plan_details.price) === 0">Free</dd>
                        <dd v-else>${{ subscription.plan_details.price }} / month</dd>

                        <dt class="text-slate-400">Plan includes</dt>
                        <dd>{{ subscription.plan_details.feature }}</dd>

                        <dt class="text-slate-400">Started</dt>
                        <dd>{{ formatLocalTimestamp(subscription.signup_timestamp_utc) }}</dd>

                        <template v-if="subscription.status === 'cancelled'">
                            <dt class="text-slate-400">Ended</dt>
                            <dd>{{ formatLocalTimestamp(subscription.cancellation_timestamp_utc) }}</dd>
                        </template>

                        <template v-if="subscription.status == 'active' && subscription.next_invoice_timestamp_utc">
                            <dt class="text-slate-400">Next invoice</dt>
                            <dd>{{ formatLocalTimestamp(subscription.next_invoice_timestamp_utc) }}</dd>
                        </template>
                    </dl>
                </section>

                <!-- Actions -->
                <section>
                    <Divider class="!my-0" />

                    <div class="flex items-center justify-between flex-wrap gap-2 pt-4">
                        <div class="flex flex-wrap gap-2">
                            <Button
                                icon="pi pi-cog"
                                severity="secondary"
                                outlined
                                @click="configOptionsShowing = !configOptionsShowing"
                                :label="configOptionsShowing ? 'Hide Settings' : 'Settings'"
                                class="border-black"
                                size="small"
                            />

                            <Button
                                icon="pi pi-credit-card"
                                v-if="subscription.plan_details.is_free === false && subscription.status !== 'cancelled'"
                                severity="secondary"
                                @click="manageBilling()"
                                label="Manage Subscription"
                                :loading="redirecting"
                                class="border-black"
                                size="small"
                            />
                            <Button
                                v-if="subscription.plan_details.is_free === false && subscription.status === 'cancelled'"
                                icon="pi pi-file"
                                severity="secondary"
                                @click="manageBilling()"
                                label="View Invoices"
                                :loading="redirecting"
                                class="border-black"
                                size="small"
                            />

                            <Button
                                v-if="subscription.status !== 'cancelled'"
                                icon="pi pi-play"
                                severity="info"
                                @click="configOptionsShowing = !configOptionsShowing"
                                label="Set Up"
                                as="a"
                                target="_blank"
                                rel="noopener"
                                href="https://the-odds-api.com/widget/v1/builder.html"
                                size="small"
                            />
                        </div>

                        <span v-if="subscription.plan_details.is_free === false" class="text-xs text-slate-400">
                            Redirects to a secure page hosted by Stripe
                        </span>
                    </div>

                    <Message severity="error" v-if="redirectError" class="mt-2">
                        {{ redirectError || 'An error occurred while redirecting to billing.' }}
                    </Message>
                </section>

                <section v-if="configOptionsShowing">
                    <Divider class="!my-0" />
                    <div class="pt-4">
                        <div class="font-bold">Affiliate Links</div>

                        <label class="text-sm text-slate-600 block my-4">

                            Add bookmaker affiliate links to the widget.

                            When your users click the bookmaker's odds in the widget, they will be redirected to the bookmaker's website using your affiliate link.

                            Many bookmakers offer affiliate programs that you can sign up for to get a unique affiliate link.

                        </label>

                        <table class="py-2 border-collapse border border-slate-200 w-full">
                            <thead>
                                <tr>
                                    <th>Bookmaker</th>
                                    <th>Link</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(formBookmaker, i) in formBookmakers"
                                    :key="'tr_' + subscription.id + '_' + i"
                                    class="border-b border-slate-200"
                                >
                                    <td class="py-2">
                                        <select :id="'bm_select_' + subscription.id + '_' + i" v-model="formBookmaker.key" class="border border-gray-300 rounded p-1">
                                            <option
                                                v-for="bookmaker in allBookmakersUnique"
                                                :selected="bookmaker.key === formBookmaker.key"
                                                :value="bookmaker.key"
                                                :key="'option_' + subscription.id + '_' + i + '_' + bookmaker.key"
                                            >
                                                {{ bookmaker.title }} ({{ bookmaker.key }})
                                            </option>
                                        </select>
                                    </td>
                                    <td class="py-2" style="width: 50%;">
                                        <InputText
                                            class="w-full"
                                            size="small"
                                            type="text"
                                            inputmode="url"
                                            v-model="formBookmaker.link"
                                            placeholder="https://example.com?affiliate_id=123"
                                            required
                                        />
                                    </td>
                                    <td>
                                        <Button
                                            severity="secondary"
                                            icon="pi pi-times"
                                            @click="removeAffiliateLink(i)"
                                            class="p-button-rounded p-button-text"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="3" class="text-center">
                                        <Button 
                                            severity="secondary"
                                            icon="pi pi-plus"
                                            @click="addFormBookmaker"
                                            label="Add Affiliate Link"
                                            class="p-button-text w-full py-4"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <div class="py-2">

                        <div :class="{disabled: subscription.plan_details.is_free}">
                            <div class="font-bold">Attribution</div>
                            <label class="text-sm text-slate-600 block my-2">
                                Show or hide the "Powered by The Odds API" banner.
                                <br />This option is only available on paid subscriptions
                            </label>
                            
                            <label for="showAttribution"  class="w-[7rem] flex border-gray-300 border p-2 text-sm uppercase cursor-pointer text-slate-800">
                                <input class="mr-2" type="checkbox" v-model="showAttribution" :disabled="subscription.plan_details.is_free || customDataSaving" id="showAttribution" />
                                {{ showAttribution ? 'Showing': 'Hidden' }}
                            </label>
                        </div>
                    </div>

                    <Button 
                        severity="info"
                        @click="handleSaveClick"
                        label="Save"
                        size="large"
                        :loading="customDataSaving"
                        class="mx-auto my-5 block"
                    />
                    <Message v-if="customDataSavingError" severity="error" class="mt-2">
                        {{ customDataSavingError }}
                    </Message>
                </section>
            </div>
        </template>
    </Card>
</template>

<style scoped>
.disabled {
    opacity: 0.5;
    pointer-events: none;
}
</style>